/**
 * Field primitives shared by the event and reminder detail panels.
 *
 * They live here rather than in `EventEditSidebar.tsx` because
 * `ReminderDetailPanel` needs them and the sidebar routes to that panel — one
 * of them importing the other directly would close an import cycle.
 */

import { GOOGLE_EVENT_COLOR_PALETTE } from "@/components/calendars/googleEventColors.ts";
import { outlookPresetToGoogleColorId } from "@/components/calendars/outlookCategoryColors.ts";
import { Button } from "@/components/ui/button.tsx";
import { Calendar } from "@/components/ui/calendar.tsx";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover.tsx";
import { Separator } from "@/components/ui/separator.tsx";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip.tsx";
import { cn } from "@/lib/utils.ts";
import type { LucideIcon } from "lucide-react";
import { ChevronDown, X } from "lucide-react";
import { useState } from "react";
import { useCalendarColors } from "./calendarColors.ts";

/** Ring styling marking the currently selected colour swatch. */
const SELECTED_SWATCH_RING = "ring-1 ring-primary ring-offset-1";

/**
 * Auto-grow textareas use the CSS `field-sizing: content` (Tailwind
 * `field-sizing-content`), capped at 10 line-heights with a scrollbar past the
 * cap — no JS height bookkeeping needed.
 *
 * Every free-text field in both detail panels needs this. Without it a textarea
 * stays at its `rows` height and long content overflows into a native scrollbar
 * that clips the text mid-line. Note that `field-sizing: content` supersedes
 * `rows`, so pair this with an explicit `min-h-*` when a field wants a floor.
 */
export const autoGrowTextareaClass = "field-sizing-content max-h-[10lh] overflow-y-auto";

/** A section's leading icon with a left-side tooltip naming the section. */
export function SectionIcon({
  icon: Icon,
  label,
  className,
}: {
  icon: LucideIcon;
  label: string;
  className?: string;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Icon className={cn("size-4 shrink-0 text-muted-foreground", className)} />
      </TooltipTrigger>
      <TooltipContent side="left">{label}</TooltipContent>
    </Tooltip>
  );
}

/**
 * Date-popover calendar styled to match the app's left-sidebar mini calendar:
 * a rounded-square selection (light-purple tint, solid "today") with no ghost
 * cell box. The `mini-cal` wrapper class activates the shared overrides in
 * `calendars/calendar.css`, which use `!important` to beat the default shadcn
 * cell/`day_selected` utilities — those default utilities fight each other and
 * caused the gray ghost box + the selection flickering/vanishing on re-render.
 */
export function DatePickerCalendar({
  selected,
  onSelect,
}: {
  selected: Date;
  onSelect: (day: Date) => void;
}) {
  return (
    // p-3 gives the grid even breathing room in the popover; `[&_table]:!ml-0`
    // cancels the mini-cal table's negative left margin, which only exists to
    // align the grid under the left sidebar's caption and would otherwise pull
    // the days flush against the popover's left edge.
    <div className="mini-cal p-3 [&_table]:!ml-0">
      <Calendar
        mode="single"
        fixedWeeks
        selected={selected}
        defaultMonth={selected}
        onSelect={(day) => day && onSelect(day)}
        initialFocus
        className="p-0"
        classNames={{
          caption: "flex justify-between pt-1 items-center",
          nav_button:
            "inline-flex items-center justify-center h-7 w-7 p-0 opacity-50 hover:opacity-100 transition-opacity cursor-pointer",
          nav_button_previous: "",
          nav_button_next: "",
        }}
      />
    </div>
  );
}

/**
 * Source-account indicator (the account row's "icon"): a dot in the event's
 * effective color — the per-event override if set, otherwise the account's
 * default calendar color. Shown in both the editable picker and the read-only
 * footer.
 */
export function ColorIndicator({
  calendarId,
  colorId,
}: {
  calendarId: string;
  colorId: string | undefined;
}) {
  const { getEventHex } = useCalendarColors();

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className="size-4 shrink-0 rounded-full border border-border"
          style={{ backgroundColor: getEventHex(calendarId, colorId) }}
        />
      </TooltipTrigger>
      <TooltipContent side="left">Source account</TooltipContent>
    </Tooltip>
  );
}

/**
 * Account color picker for the footer. The color swatch is a static indicator
 * (the "icon"); the calendar/account name is the clickable trigger — matching the
 * other sections' "unclickable icon, clickable content" pattern — and the palette
 * popover spawns from the name.
 */
export function EventColorPicker({
  calendarId,
  colorId,
  onChange,
  calendarName,
}: {
  calendarId: string;
  colorId: string | undefined;
  onChange: (id: string | undefined) => void;
  calendarName?: string;
}) {
  // Controlled so a swatch click can close the popover immediately. Whether the
  // pick changed the color or not, we close; the form's dirty check (colorDirty)
  // independently decides whether the Save/Cancel footer appears.
  const [open, setOpen] = useState(false);
  const pick = (id: string | undefined) => {
    onChange(id);
    setOpen(false);
  };
  // The palette uses unified Google ids ("1"-"11"). An Outlook event stores a
  // preset ("preset3"), so normalize to the matching swatch id for the selected
  // highlight. The raw colorId is still passed to the indicator dot below so it
  // renders the event's true color (the render resolver handles both schemes).
  const selectedSwatchId = outlookPresetToGoogleColorId(colorId) ?? colorId;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className="flex min-w-0 items-center gap-2">
        <ColorIndicator calendarId={calendarId} colorId={colorId} />
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="xs"
            data-statsig-name="event-color-trigger"
            className="min-w-0 justify-between gap-1"
          >
            <span className="truncate">{calendarName ?? "Event color"}</span>
            <ChevronDown className="size-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
      </div>
      {/* onOpenAutoFocus is prevented because Radix focuses the first swatch on
          open and Radix Tooltip fires on focus with no delay — that flashed the
          "Default" tooltip open every time the popover opened, pointer nowhere
          near it. With focus suppressed, tooltips only show on real hover, after
          the standard delay. */}
      <PopoverContent
        align="start"
        className="w-60 p-2"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {/* Source account — makes clear which account this color applies to. */}
        <div className="flex items-center gap-2 px-2 py-1.5">
          <ColorIndicator calendarId={calendarId} colorId={colorId} />
          <span className="truncate text-sm font-medium">{calendarName ?? "Event color"}</span>
        </div>
        <Separator className="-mx-2 my-1.5 w-auto bg-muted" />
        <div className="px-2 pb-1.5 text-xs font-medium text-muted-foreground">Event color</div>
        <div className="flex flex-wrap items-center gap-1.5 px-2 pb-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label="Default color"
                data-statsig-name="event-color-default"
                onClick={() => pick(undefined)}
                className={cn(
                  "size-5 rounded-full border border-border bg-card p-0",
                  selectedSwatchId === undefined && SELECTED_SWATCH_RING,
                )}
              >
                <X className="size-3 text-muted-foreground" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">Default</TooltipContent>
          </Tooltip>
          {GOOGLE_EVENT_COLOR_PALETTE.map((c) => (
            <Tooltip key={c.id}>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={c.name}
                  data-statsig-name={`event-color-${c.id}`}
                  onClick={() => pick(c.id)}
                  style={{ backgroundColor: c.hex }}
                  className={cn(
                    "size-5 rounded-full border border-border/40 p-0 hover:opacity-90",
                    selectedSwatchId === c.id && SELECTED_SWATCH_RING,
                  )}
                />
              </TooltipTrigger>
              <TooltipContent side="top">{c.name}</TooltipContent>
            </Tooltip>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
